/**
 * Barra de acciones sobre la selección de la tabla (§7).
 *
 * Descartar en lote es reversible y se ofrece con deshacer. Fusionar exige
 * elegir cuál de los seleccionados se conserva: la operación no es simétrica
 * —el destino se queda con el nombre— y adivinarlo sería una mala idea.
 */

import { Download, GitMerge, Trash2, X } from 'lucide-react'
import { useState } from 'react'

import { api } from '../../api/cliente'
import {
  useDescartarStakeholder,
  useFusionar,
  useRestaurarStakeholder,
  useTabla,
} from '../../api/hooks'
import type { StakeholderFila } from '../../api/tipos'
import { mensajeDeError, useAvisos } from '../../ui/avisos-contexto'
import { Confirmacion, Dialogo } from '../../ui/Dialogo'
import { Boton, EtiquetaTipo } from '../../ui/primitivos'
import { cx } from '../../ui/util'

export function AccionesMasivas({
  entidadId,
  ids,
  onLimpiar,
}: {
  entidadId: number
  ids: number[]
  onLimpiar: () => void
}) {
  const avisos = useAvisos()
  const descartar = useDescartarStakeholder(entidadId)
  const restaurar = useRestaurarStakeholder(entidadId)
  const fusionar = useFusionar(entidadId)

  const [confirmarDescarte, setConfirmarDescarte] = useState(false)
  const [eligiendoDestino, setEligiendoDestino] = useState(false)
  const [destinoId, setDestinoId] = useState<number | null>(null)

  // Las fichas seleccionadas solo hacen falta al elegir destino de la fusión,
  // así que la consulta se activa entonces y no al marcar una casilla.
  const tabla = useTabla(eligiendoDestino ? entidadId : undefined, { page: 1 })
  const seleccionadas: StakeholderFila[] = (tabla.data?.results ?? []).filter((f) =>
    ids.includes(f.id),
  )

  async function descartarTodos() {
    const resultados = await Promise.allSettled(
      ids.map((id) => descartar.mutateAsync({ id, motivo: 'Descarte masivo desde la tabla' })),
    )
    const ok = resultados.filter((r) => r.status === 'fulfilled').length
    const fallidos = resultados.length - ok

    if (ok > 0) {
      avisos.exito(`${ok} stakeholder${ok === 1 ? '' : 's'} descartado${ok === 1 ? '' : 's'}`, {
        detalle: 'Nada se borró: puedes restaurarlos cuando quieras.',
        deshacer: {
          etiqueta: `Restaurar ${ok}`,
          fn: () => {
            for (const id of ids) restaurar.mutate(id)
          },
        },
      })
    }
    if (fallidos > 0) {
      avisos.error(`${fallidos} no se pudieron descartar`, 'Revisa los logs del backend.')
    }
    setConfirmarDescarte(false)
    onLimpiar()
  }

  function fusionarTodos() {
    if (destinoId === null) return
    const origenes = ids.filter((id) => id !== destinoId)
    fusionar.mutate(
      { destinoId, origenIds: origenes },
      {
        onSuccess: (destino) => {
          avisos.exito(`${origenes.length} registros fusionados en «${destino.nombre}»`)
          setEligiendoDestino(false)
          setDestinoId(null)
          onLimpiar()
        },
        onError: (error) => avisos.error('No se pudo fusionar', mensajeDeError(error)),
      },
    )
  }

  const nombreDestino = seleccionadas.find((f) => f.id === destinoId)?.nombre

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-borde)] bg-[var(--color-acento-suave)] px-3 py-1.5 text-[12px]">
        <span className="font-medium">
          {ids.length} seleccionado{ids.length === 1 ? '' : 's'}
        </span>

        <Boton
          variante="sutil"
          icono={<Trash2 size={13} />}
          onClick={() => setConfirmarDescarte(true)}
        >
          Descartar
        </Boton>

        <Boton
          variante="sutil"
          icono={<GitMerge size={13} />}
          disabled={ids.length < 2}
          onClick={() => {
            setDestinoId(ids[0] ?? null)
            setEligiendoDestino(true)
          }}
          title={
            ids.length < 2
              ? 'Hacen falta al menos dos registros para fusionar'
              : 'Unir los seleccionados en uno solo'
          }
        >
          Fusionar
        </Boton>

        <a
          href={api.urlDescarga(`/entities/${entidadId}/export/?format=csv`)}
          download
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium text-[var(--color-texto-suave)] hover:bg-[var(--color-panel)] hover:text-[var(--color-texto)]"
          title="La exportación incluye todos los stakeholders activos, no solo los seleccionados"
        >
          <Download size={13} />
          Exportar todo
        </a>

        <Boton variante="sutil" icono={<X size={13} />} onClick={onLimpiar} className="ml-auto">
          Deseleccionar
        </Boton>
      </div>

      <Confirmacion
        abierto={confirmarDescarte}
        onCerrar={() => setConfirmarDescarte(false)}
        onConfirmar={descartarTodos}
        titulo={`Descartar ${ids.length} stakeholder${ids.length === 1 ? '' : 's'}`}
        descripcion="Desaparecerán del grafo y de la tabla, pero no se borran: puedes restaurarlos en cualquier momento."
        etiquetaConfirmar="Descartar"
        cargando={descartar.isPending}
      />

      <Dialogo
        abierto={eligiendoDestino}
        onCerrar={() => setEligiendoDestino(false)}
        titulo={`Fusionar ${ids.length} registros`}
        descripcion="Elige cuál se conserva. Los demás pasarán a ser sus alias y aportarán sus listas y fuentes."
        pie={
          <>
            <Boton
              variante="secundario"
              onClick={() => setEligiendoDestino(false)}
              disabled={fusionar.isPending}
            >
              Cancelar
            </Boton>
            <Boton
              variante="primario"
              icono={<GitMerge size={13} />}
              disabled={destinoId === null}
              cargando={fusionar.isPending}
              onClick={fusionarTodos}
            >
              Fusionar en «{nombreDestino ?? '—'}»
            </Boton>
          </>
        }
      >
        {seleccionadas.length < ids.length && (
          <p className="mb-2 rounded-md border border-[var(--color-critica)] px-2.5 py-1.5 text-[12px]">
            Algunos seleccionados están en otra página de la tabla y no se pueden mostrar aquí.
            La fusión los incluirá igualmente.
          </p>
        )}
        <ul className="divide-y divide-[var(--color-borde)] rounded-md border border-[var(--color-borde)]">
          {seleccionadas.map((fila) => (
            <li key={fila.id}>
              <button
                onClick={() => setDestinoId(fila.id)}
                className={cx(
                  'flex w-full items-center gap-2 px-2.5 py-2 text-left',
                  destinoId === fila.id
                    ? 'bg-[var(--color-acento-suave)]'
                    : 'hover:bg-[var(--color-acento-suave)]',
                )}
              >
                <input
                  type="radio"
                  checked={destinoId === fila.id}
                  readOnly
                  className="shrink-0 accent-[var(--color-acento)]"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{fila.nombre}</span>
                  <span className="mt-0.5 flex items-center gap-2 text-[11px] text-[var(--color-texto-tenue)]">
                    <EtiquetaTipo tipo={fila.tipo} />
                    <span>{fila.n_fuentes} fuentes</span>
                  </span>
                </span>
                {destinoId === fila.id && (
                  <span className="shrink-0 text-[11px] font-medium text-[var(--color-acento)]">
                    se conserva
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </Dialogo>
    </>
  )
}
